import { clsx } from 'clsx';
import { METAMAGIC_OPTIONS, getMetamagicCount, getMetamagicOption } from '@character-creator/data';

interface MetamagicSelectorProps {
  sorcererLevel: number;
  selectedMetamagic: string[];
  onToggle: (metamagicId: string) => void;
  compact?: boolean;
}

export function MetamagicSelector({
  sorcererLevel,
  selectedMetamagic,
  onToggle,
  compact = false,
}: MetamagicSelectorProps) {
  const allowedCount = getMetamagicCount(sorcererLevel);
  const selectedCount = selectedMetamagic.length;
  const needsMore = selectedCount < allowedCount;

  // Get selected metamagic names for compact display
  const selectedNames = selectedMetamagic
    .map((id) => getMetamagicOption(id)?.name)
    .filter(Boolean);

  // Compact view - shows selected metamagic
  if (compact && !needsMore && selectedNames.length > 0) {
    return (
      <div className="text-xs text-success">
        <span>Metamagic: {selectedNames.join(', ')}</span>
      </div>
    );
  }

  // Don't show if sorcerer isn't high enough level
  if (sorcererLevel < 3) {
    return (
      <div className="text-xs text-text-muted">
        Metamagic available at level 3
      </div>
    );
  }

  return (
    <div>
      <h5 className="text-sm font-medium text-text-primary mb-2">
        Metamagic
        {needsMore && (
          <span className="text-warning ml-2 text-xs">
            (Choose {allowedCount - selectedCount} more)
          </span>
        )}
      </h5>
      <p className="text-xs text-text-muted mb-3">
        You gain the ability to twist your spells to suit your needs. You can use
        Sorcery Points to fuel Metamagic options.
      </p>
      <div className="grid grid-cols-1 gap-2">
        {METAMAGIC_OPTIONS.map((metamagic) => {
          const isSelected = selectedMetamagic.includes(metamagic.id);
          const isDisabled = !isSelected && selectedCount >= allowedCount;

          return (
            <button
              key={metamagic.id}
              type="button"
              onClick={() => onToggle(metamagic.id)}
              disabled={isDisabled}
              className={clsx(
                'w-full text-left p-3 rounded-lg border transition-all duration-200',
                isSelected
                  ? 'bg-accent-gold/10 border-accent-gold'
                  : isDisabled
                    ? 'bg-background-secondary border-background-elevated opacity-50 cursor-not-allowed'
                    : 'bg-background-secondary border-background-elevated hover:border-text-muted'
              )}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-text-primary text-sm">{metamagic.name}</span>
                <span className="text-xs text-accent-gold">{metamagic.cost}</span>
              </div>
              <p className="text-xs text-text-muted mt-1 line-clamp-2">{metamagic.description}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
